import { useMediaQuery } from "@gluestack-ui/utils/hooks";
import { SearchX } from "lucide-react-native";
import { FC, useMemo } from "react";

import { Box, Center, Pressable, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import type { GeographicMapConfig } from "@/types/resources";

import { CONTENT_HEIGHT } from "./constants";
import { CountryMap } from "./maps/CountryMap";

export interface StateEditorProps {
  disabled: boolean;
  mapConfig: GeographicMapConfig | null;
  onToggleState: (code: string) => void;
  search: string;
  states: { label: string; value: string }[];
  stateValues: string[];
  viewMode: "list" | "map";
}

const StateEditor: FC<StateEditorProps> = ({
  disabled,
  mapConfig,
  onToggleState,
  search,
  states,
  stateValues,
  viewMode,
}) => {
  const { i18n } = useLocalization();
  const [isDesktop] = useMediaQuery({ minWidth: 1024 });

  const filteredStates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return states.filter((s) => !q || s.label.toLowerCase().includes(q));
  }, [search, states]);

  const showMap = viewMode === "map" && !!mapConfig;

  if (showMap) {
    return (
      <CountryMap
        disabled={disabled}
        key={mapConfig.url}
        mapConfig={mapConfig}
        onToggleState={onToggleState}
        stateValues={stateValues}
      />
    );
  }

  return (
    <Box
      className="bg-background-0"
      // @ts-expect-error web-only CSS overflowY
      style={{ height: CONTENT_HEIGHT, overflowY: "auto" }}
    >
      {filteredStates.length === 0 ? (
        <Center className="py-12">
          <VStack className="items-center gap-3">
            <SearchX className="text-typography-400" size={48} />
            <Text className="text-lg font-medium text-typography-700">
              {i18n.t("misc.noSearchResults")}
            </Text>
            <Text className="text-sm text-typography-500">
              {i18n.t("misc.noSearchResultsDescription")}
            </Text>
          </VStack>
        </Center>
      ) : (
        <Box
          className="p-4"
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          {filteredStates.map((item) => {
            const isSelected = stateValues.includes(item.value);
            return (
              <Pressable
                key={item.value}
                onPress={() => onToggleState(item.value)}
                // @ts-expect-error web-only CSS
                style={{
                  cursor: disabled ? "default" : "pointer",
                  width: isDesktop ? "calc(25% - 6px)" : "calc(50% - 4px)",
                }}
              >
                <Box
                  className={`rounded-lg border ${isSelected ? "bg-primary-50 border-primary-200" : "bg-transparent border-outline-100"}`}
                  style={{ padding: 10 }}
                >
                  <Text
                    className={`text-xs ${isSelected ? "text-primary-700 font-semibold" : "text-typography-700"}`}
                    numberOfLines={1}
                  >
                    {item.label}
                  </Text>
                </Box>
              </Pressable>
            );
          })}
          <Box style={{ height: 8, width: "100%" }} />
        </Box>
      )}
    </Box>
  );
};

export default StateEditor;
